import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Copy, Check, Instagram, Music2, Globe, Linkedin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Magnetic } from "@/components/site/motion";
import ContactForm from "@/components/site/ContactForm";
import { EMAIL, SOCIALS } from "@/lib/constants";

const SOCIAL_ICONS = { Instagram, TikTok: Music2, Behance: Globe, LinkedIn: Linkedin, WhatsApp: MessageCircle };

export default function Footer() {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy email");
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-white/[0.08]" data-testid="site-footer">
      <div className="absolute -top-32 left-1/2 h-72 w-[720px] -translate-x-1/2 rounded-full bg-gradient-to-tr from-neutral-500 to-stone-300 opacity-[0.08] blur-3xl" />

      <div className="relative mx-auto w-[92%] max-w-7xl py-24 md:py-32">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-6">Contact</p>
              <h2 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="contact-headline">
                Have a project<br />in mind?
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-neutral-400 md:text-lg">
                Let&apos;s create something meaningful, distinctive, and visually memorable.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Magnetic>
                  <a href={`mailto:${EMAIL}`} className="btn-primary text-base" data-testid="start-project-cta">
                    Start a Project <ArrowUpRight className="h-5 w-5" />
                  </a>
                </Magnetic>
                <button
                  onClick={copyEmail}
                  data-testid="copy-email-button"
                  className="btn-ghost"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {EMAIL}
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                {SOCIALS.map((s) => {
                  const Icon = SOCIAL_ICONS[s.label] || Globe;
                  return (
                    <a
                      key={s.label}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-link-${s.label.toLowerCase()}`}
                      className="glass glass-hover group flex flex-col gap-2 rounded-2xl p-4"
                    >
                      <Icon className="h-4 w-4 text-neutral-400 transition-colors group-hover:text-white" />
                      <span className="text-sm font-medium text-white">{s.label}</span>
                      <span className="truncate text-xs text-neutral-500">{s.handle}</span>
                    </a>
                  );
                })}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-5">
            <ContactForm />
          </Reveal>
        </div>

        <div className="mt-24 flex flex-col items-start justify-between gap-6 border-t border-white/[0.08] pt-8 md:flex-row md:items-center">
          <span className="font-display text-lg font-extrabold tracking-tight text-white">
            JARE
          </span>
          <div className="flex flex-wrap gap-6">
            {["Work", "About", "Blog", "Contact"].map((l) => (
              <Link
                key={l}
                to={`/${l.toLowerCase()}`}
                data-testid={`footer-link-${l.toLowerCase()}`}
                className="text-sm text-neutral-500 transition-colors hover:text-white"
              >
                {l}
              </Link>
            ))}
            <Link to="/admin/login" data-testid="footer-link-admin" className="text-sm text-neutral-600 transition-colors hover:text-white">
              Admin
            </Link>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-600">
            © 2026 JARE — Design · Motion · Video
          </p>
        </div>
      </div>
    </footer>
  );
}
